import type { CreateVehicleRequest, VehicleDTO } from '@wf/shared'

// Gemeinsame Fahrzeugschein-Stammdaten (Zulassungsbescheinigung Teil I) für Anlege- UND Bearbeiten-Formular.
// Rohwerte als Strings (Formular-Eingabe); Zahlen/Datum werden erst beim Absenden geparst (leer → null).
// Kennzeichen (Pflichtfeld) bleibt bewusst im jeweiligen Parent-Formular — hier nur die optionalen Zusatzfelder.

/** Editier-Rohwerte der optionalen Stammdaten (alles String; Kennzeichen separat im Parent). */
export interface VehicleFieldValues {
  make: string
  model: string
  vin: string
  hsn: string
  tsn: string
  firstRegistration: string
  displacementCcm: string
  powerKw: string
  fuelType: string
}

export const EMPTY_VEHICLE_FIELDS: VehicleFieldValues = {
  make: '',
  model: '',
  vin: '',
  hsn: '',
  tsn: '',
  firstRegistration: '',
  displacementCcm: '',
  powerKw: '',
  fuelType: '',
}

/** Bestehendes Fahrzeug → Formular-Rohwerte (null → '' bzw. Zahl → String). */
export function vehicleToFields(v: VehicleDTO): VehicleFieldValues {
  return {
    make: v.make ?? '',
    model: v.model ?? '',
    vin: v.vin ?? '',
    hsn: v.hsn ?? '',
    tsn: v.tsn ?? '',
    firstRegistration: v.firstRegistration ?? '',
    displacementCcm: v.displacementCcm != null ? String(v.displacementCcm) : '',
    powerKw: v.powerKw != null ? String(v.powerKw) : '',
    fuelType: v.fuelType ?? '',
  }
}

/** Nur die Stammdaten-Felder des API-Bodys (Kennzeichen/version/customerId ergänzt der Aufrufer). */
export type VehicleFieldsBody = Pick<
  CreateVehicleRequest,
  'make' | 'model' | 'vin' | 'hsn' | 'tsn' | 'firstRegistration' | 'displacementCcm' | 'powerKw' | 'fuelType'
>

/** Rohwerte → API-Body-Anteil: leere Felder → null, Zahlen geparst (leer/ungültig/negativ → null). */
export function vehicleFieldsToBody(f: VehicleFieldValues): VehicleFieldsBody {
  const s = (x: string): string | null => (x.trim() ? x.trim() : null)
  const n = (x: string): number | null => {
    const v = Number(x.trim())
    return x.trim() && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null
  }
  return {
    make: s(f.make),
    model: s(f.model),
    vin: s(f.vin),
    hsn: s(f.hsn),
    tsn: s(f.tsn),
    firstRegistration: s(f.firstRegistration),
    displacementCcm: n(f.displacementCcm),
    powerKw: n(f.powerKw),
    fuelType: s(f.fuelType),
  }
}

const FUEL_OPTIONS = ['Benzin', 'Diesel', 'Elektro', 'Hybrid', 'LPG (Autogas)', 'CNG (Erdgas)', 'Wasserstoff']

/**
 * Die Fahrzeugschein-Eingabefelder (Marke/Modell/HSN/TSN/FIN/Erstzulassung/Motor). Die Klammer-Hinweise
 * („zu 2.1" …) entsprechen den Feldpositionen der Zulassungsbescheinigung Teil I. Accessible Name je Feld
 * = der schlichte Feldname (aria-label), damit Formular-Tests stabil danach greifen können.
 */
export function VehicleDetailFields({
  value,
  onChange,
}: {
  value: VehicleFieldValues
  onChange: (patch: Partial<VehicleFieldValues>) => void
}) {
  return (
    <>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Marke</span>
          <input className="wf-input" aria-label="Marke" value={value.make} onChange={(e) => onChange({ make: e.target.value })} placeholder="VW" />
        </label>
        <label className="wf-field">
          <span className="wf-label">Modell</span>
          <input className="wf-input" aria-label="Modell" value={value.model} onChange={(e) => onChange({ model: e.target.value })} placeholder="Golf" />
        </label>
      </div>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">HSN <span className="muted">(zu 2.1)</span></span>
          <input
            className="wf-input"
            aria-label="HSN"
            inputMode="numeric"
            value={value.hsn}
            onChange={(e) => onChange({ hsn: e.target.value.toUpperCase() })}
            placeholder="0603"
          />
        </label>
        <label className="wf-field">
          <span className="wf-label">TSN <span className="muted">(zu 2.2)</span></span>
          <input
            className="wf-input"
            aria-label="TSN"
            value={value.tsn}
            onChange={(e) => onChange({ tsn: e.target.value.toUpperCase() })}
            placeholder="BGU"
          />
        </label>
      </div>
      <label className="wf-field">
        <span className="wf-label">FIN / VIN <span className="muted">(zu E)</span></span>
        <input className="wf-input" aria-label="FIN" value={value.vin} onChange={(e) => onChange({ vin: e.target.value.toUpperCase() })} />
      </label>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Erstzulassung <span className="muted">(zu B)</span></span>
          <input
            className="wf-input"
            aria-label="Erstzulassung"
            type="date"
            value={value.firstRegistration}
            onChange={(e) => onChange({ firstRegistration: e.target.value })}
          />
        </label>
        <label className="wf-field">
          <span className="wf-label">Kraftstoff <span className="muted">(zu P.3)</span></span>
          <input
            className="wf-input"
            aria-label="Kraftstoff"
            list="wf-fuel-options"
            value={value.fuelType}
            onChange={(e) => onChange({ fuelType: e.target.value })}
            placeholder="Diesel"
          />
          <datalist id="wf-fuel-options">
            {FUEL_OPTIONS.map((o) => (
              <option key={o} value={o} />
            ))}
          </datalist>
        </label>
      </div>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Hubraum in cm³ <span className="muted">(zu P.1)</span></span>
          <input
            className="wf-input"
            aria-label="Hubraum"
            inputMode="numeric"
            value={value.displacementCcm}
            onChange={(e) => onChange({ displacementCcm: e.target.value })}
            placeholder="1968"
          />
        </label>
        <label className="wf-field">
          <span className="wf-label">Leistung in kW <span className="muted">(zu P.2)</span></span>
          <input
            className="wf-input"
            aria-label="Leistung"
            inputMode="numeric"
            value={value.powerKw}
            onChange={(e) => onChange({ powerKw: e.target.value })}
            placeholder="110"
          />
        </label>
      </div>
    </>
  )
}
